import { apiClient } from './client';
import type {
  InventarioDashboard,
  HistorialActivoResponse,
  EmpleadoBusqueda,
  EspacioFisico,
} from '@/types/dominio';

/**
 * KPIs del panel. `origen` filtra por fuente (GLPI/Compras). El backend además
 * fuerza el filtro a COMPRAS para usuarios con rol COMPRAS (no ven GLPI).
 */
export async function getInventarioDashboard(
  origen?: 'GLPI' | 'COMPRAS',
): Promise<InventarioDashboard> {
  const { data } = await apiClient.get<InventarioDashboard>('/inventario/dashboard', {
    params: origen ? { origen } : undefined,
  });
  return data;
}

/**
 * Línea de tiempo del activo (HU-4.4).
 * Cabecera del activo + eventos del historial ordenados DESC por fecha.
 */
export async function getHistorialActivo(idItem: string): Promise<HistorialActivoResponse> {
  const { data } = await apiClient.get<HistorialActivoResponse>(`/inventario/${idItem}/historial`);
  return data;
}

// ── Listado de inventario (lista navegable GLPI + Compras) ────────────────

export type EstadoItem = 'EN_BODEGA' | 'ASIGNADO' | 'EN_REPARACION' | 'DADO_DE_BAJA';
export type OrigenDatos = 'GLPI' | 'COMPRAS';
export type CondicionElemento = 'EXCELENTE' | 'BUENO' | 'EN_REPARACION' | 'DANADO' | 'BAJA';

export interface InventarioItem {
  id_item_maestro: string;
  serial_global: string;
  estado_actual: EstadoItem;
  /** Condición física — independiente de la asignación. Null en items GLPI (hasta Fase 2) */
  condicion: CondicionElemento | null;
  /** Ubicación física: "Oficina" por defecto, dirección del empleado en teletrabajo. */
  ubicacion: string | null;
  origen_datos: OrigenDatos;
  /** Tipo unificado: ag.tipo (GLPI) o ic.tipo_objeto (Compras) */
  tipo: string | null;
  /** Especificación — solo items de Compras */
  especificacion: string | null;
  /** Nombre técnico GLPI — solo items de GLPI */
  nombre_glpi: string | null;
  marca: string | null;
  modelo: string | null;
  empleado_asignado: { id: string; nombres: string; documento: string } | null;
  /** Espacio físico asignado — solo cuando el activo no tiene empleado. */
  espacio_asignado: EspacioFisico | null;
  /** Fecha del último evento ASIGNADO — desde cuándo la tiene el colaborador. */
  fecha_asignacion: string | null;
}

export interface ListInventarioParams {
  page?: number;
  pageSize?: number;
  busqueda?: string;
  estado?: EstadoItem;
  origen?: OrigenDatos;
}

export interface ListInventarioResponse {
  items: InventarioItem[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * GET /inventario/items — listado paginado del catálogo completo.
 * Envía solo los params definidos para no ensuciar la query string.
 */
export async function listarInventario(
  params: ListInventarioParams = {},
): Promise<ListInventarioResponse> {
  const query: Record<string, string | number> = {};
  if (params.page) query.page = params.page;
  if (params.pageSize) query.pageSize = params.pageSize;
  if (params.busqueda?.trim()) query.busqueda = params.busqueda.trim();
  if (params.estado) query.estado = params.estado;
  if (params.origen) query.origen = params.origen;

  const { data } = await apiClient.get<ListInventarioResponse>('/inventario/items', {
    params: query,
  });
  return data;
}

export interface AsignarLotePayload {
  id_empleado: string;
  ids_items: string[];
  tipo_ubicacion?: TipoUbicacion;
  observaciones?: string;
}

export interface AsignarLoteResponse {
  empleado: { id: string; nombres: string; documento: string };
  asignados: Array<{
    id_item_maestro: string;
    serial_global: string;
    estado_actual: EstadoItem;
    ubicacion: string;
  }>;
}

export async function darDeBaja(
  idItemMaestro: string,
  payload: { observaciones?: string },
): Promise<{ id_item_maestro: string; serial_global: string; estado_actual: EstadoItem; empleado_desasignado: boolean }> {
  const { data } = await apiClient.post(`/inventario/${idItemMaestro}/dar-de-baja`, payload);
  return data;
}

export async function asignarLote(payload: AsignarLotePayload): Promise<AsignarLoteResponse> {
  const { data } = await apiClient.post<AsignarLoteResponse>('/inventario/asignar-lote', payload);
  return data;
}

export async function actualizarCondicion(
  idItemMaestro: string,
  condicion: CondicionElemento,
): Promise<{ id_item_maestro: string; serial_global: string; condicion: CondicionElemento; estado_actual: EstadoItem }> {
  const { data } = await apiClient.patch(`/inventario/${idItemMaestro}/condicion`, { condicion });
  return data;
}

export async function getNextSerial(tipo: string): Promise<{ serial: string }> {
  const { data } = await apiClient.get<{ serial: string }>('/inventario/next-serial', {
    params: { tipo },
  });
  return data;
}

// ── Catálogo de tipos de objeto de Compras ────────────────────────────────

export interface TipoObjeto {
  id_tipo: string;
  nombre: string;
  /** Subcategorías sugeridas; vacío = especificación de texto libre. */
  especificaciones: string[];
}

export interface CrearTipoObjetoPayload {
  nombre: string;
  especificaciones?: string[];
}

export async function getTiposObjeto(): Promise<TipoObjeto[]> {
  const { data } = await apiClient.get<TipoObjeto[]>('/inventario/tipos-objeto');
  return data;
}

export async function crearTipoObjeto(payload: CrearTipoObjetoPayload): Promise<TipoObjeto> {
  const { data } = await apiClient.post<TipoObjeto>('/inventario/tipos-objeto', payload);
  return data;
}

// ── Alta de item de Compras (HU-4.2) ──────────────────────────────────────

export interface CrearItemComprasPayload {
  serial_global: string;
  tipo_objeto: string;
  especificacion: string;
  condicion: CondicionElemento;
}

export interface CrearItemComprasResponse {
  id_item_maestro: string;
  serial_global: string;
  tipo_objeto: string;
  especificacion: string;
  condicion: CondicionElemento;
  estado_actual: 'EN_BODEGA';
  origen_datos: 'COMPRAS';
}

/**
 * POST /inventario — alta manual de un item del inventario de Compras.
 * Solo ADMIN y COMPRAS. El item nace EN_BODEGA, listo para asignar.
 */
export async function crearItemCompras(
  payload: CrearItemComprasPayload,
): Promise<CrearItemComprasResponse> {
  const { data } = await apiClient.post<CrearItemComprasResponse>('/inventario', payload);
  return data;
}

// ── Asignación de equipos (HU-4.3) ────────────────────────────────────────

/**
 * Busca empleados por nombre o documento (máx 10 resultados).
 * Si q es vacío devuelve los 10 primeros para mostrar opciones iniciales.
 */
export async function buscarEmpleados(q: string): Promise<EmpleadoBusqueda[]> {
  const { data } = await apiClient.get<EmpleadoBusqueda[]>('/inventario/empleados/buscar', {
    params: q ? { q } : undefined,
  });
  return data;
}

/**
 * GET /inventario/espacios — catálogo de espacios físicos activos, para la
 * pestaña "OFICINA" del dialog de asignación (activos de Compras que no
 * van a una persona).
 */
export async function getEspaciosActivos(): Promise<EspacioFisico[]> {
  const { data } = await apiClient.get<EspacioFisico[]>('/inventario/espacios');
  return data;
}

/** Modalidad de ubicación del activo — el backend resuelve la dirección concreta. */
export type TipoUbicacion = 'OFICINA' | 'TELETRABAJO';

export interface AsignarEquipoPayload {
  /** Colaborador que recibe el equipo. Mutuamente excluyente con `id_espacio`. */
  id_empleado?: string;
  /** Espacio físico destino (solo activos de Compras). Mutuamente excluyente con `id_empleado`. */
  id_espacio?: string;
  observaciones?: string;
  /**
   * Modalidad de ubicación cuando el destino es un empleado. Si se omite,
   * el backend asume OFICINA. En TELETRABAJO el backend toma la dirección
   * registrada del empleado. No aplica cuando el destino es un espacio.
   */
  tipo_ubicacion?: TipoUbicacion;
  /** Imágenes opcionales — fotos del equipo al momento de la entrega */
  imagenes?: File[];
}

export interface AsignarEquipoResponse {
  id_historial: string;
  id_item_maestro: string;
  serial_global: string;
  estado_actual: 'ASIGNADO';
  ubicacion: string | null;
  empleado: { id: string; nombres: string; documento: string } | null;
  espacio: EspacioFisico | null;
  imagenes_subidas: number;
}

/**
 * POST /inventario/:id/asignar — entrega del equipo a un empleado o lo
 * ubica en un espacio físico.
 *
 * Usa multipart/form-data porque viaja con imágenes. El backend acepta
 * 0 o N imágenes bajo el campo "imagenes" (mismo nombre, varios files).
 */
export async function asignarEquipo(
  idItem: string,
  payload: AsignarEquipoPayload,
): Promise<AsignarEquipoResponse> {
  const formData = new FormData();
  if (payload.id_empleado) {
    formData.append('id_empleado', payload.id_empleado);
  }
  if (payload.id_espacio) {
    formData.append('id_espacio', payload.id_espacio);
  }
  if (payload.observaciones) {
    formData.append('observaciones', payload.observaciones);
  }
  if (payload.tipo_ubicacion) {
    formData.append('tipo_ubicacion', payload.tipo_ubicacion);
  }
  for (const img of payload.imagenes ?? []) {
    formData.append('imagenes', img);
  }

  const { data } = await apiClient.post<AsignarEquipoResponse>(
    `/inventario/${idItem}/asignar`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

// ── Devolver equipo ───────────────────────────────────────────────────────

export interface DevolverEquipoPayload {
  observaciones?: string;
}

export interface DevolverEquipoResponse {
  id_historial: string;
  id_item_maestro: string;
  serial_global: string;
  estado_actual: EstadoItem;
  ubicacion: string;
}

export async function devolverEquipo(
  idItemMaestro: string,
  payload: DevolverEquipoPayload,
): Promise<DevolverEquipoResponse> {
  const { data } = await apiClient.post<DevolverEquipoResponse>(
    `/inventario/${idItemMaestro}/devolver`,
    payload,
  );
  return data;
}

// ── Builders de URL para archivos servidos como estáticos ────────────────

/**
 * Construye la URL absoluta para descargar un PDF del historial.
 *
 * El backend devuelve `ruta_archivo_pdf` como ruta relativa al directorio
 * estático de uploads (ej. "uploads/mantenimientos/2026/abc.pdf"). Aquí la
 * convertimos en URL absoluta usando la misma base que el resto de la API,
 * pero SIN el sufijo `/api/v1` — los uploads se sirven desde la raíz del
 * backend (configurado en main.ts con `app.useStaticAssets`).
 */
export function buildPdfUrl(rutaRelativa: string): string {
  const apiBase = import.meta.env.VITE_API_BASE_URL?.toString() ?? '';
  // Si ya viene absoluta (http/https), devolvemos tal cual
  if (/^https?:\/\//i.test(rutaRelativa)) return rutaRelativa;
  // Garantizar leading slash
  const path = rutaRelativa.startsWith('/') ? rutaRelativa : `/${rutaRelativa}`;
  // Si no hay VITE_API_BASE_URL (dev con proxy de Vite), devolvemos solo el path
  return apiBase ? `${apiBase}${path}` : path;
}

/** Alias semántico — misma lógica que buildPdfUrl, sirve para imágenes de entrega. */
export const buildImagenUrl = buildPdfUrl;
